import { MapElementController } from '@sas-dvr/ltjs-bird';
import { VisualElementControllerLoader } from './LoaderFactory';
import { map, tap, distinctUntilChanged } from 'rxjs/operators';
import { ZoomState } from '@sas-dvr/internal-va-react-core/store/zoom/zoomState';
import { ofActionCreatorType } from '../util/rxUtils';
import { EMPTY, fromEventPattern, merge } from 'rxjs';
import { loadGraphElementController } from './loadGraphElement';
import { VisualizationManager } from '@sas-dvr/ltjs-commons';
import { fromPortableEvent } from '../util';
import { LayoutOverlayMap } from '@sas-dvr/ltjs-gtl';

export const loadMapElementController: VisualElementControllerLoader = (
  visualController,
  visualElementController,
  unload$,
  myActions$,
  state$,
  deps,
  lifecyleActions
) => {
  if (!(visualElementController instanceof MapElementController)) {
    throw new Error('Expected the controller to be a MapElementController');
  }
  const key = deps.getKey(visualElementController);

  const visualizationManager = new VisualizationManager();

  const layout = visualElementController.getGraphModel()?.getLayout();
  const lockNavigation = layout instanceof LayoutOverlayMap ? layout.getLockNavigation() : false;

  const loadZoomControls$ = lockNavigation
    ? EMPTY
    : merge(
        // Fired on pan, but not on reset view
        fromPortableEvent(visualElementController, MapElementController.MAP_BOUNDS_CHANGED),
        // Fired on pan and reset view, but not until map tiles load
        fromEventPattern((handler) => visualizationManager.setFrameValidCallback(handler)),
        myActions$.pipe(
          ofActionCreatorType<typeof ZoomState.actions.zoomIn>(ZoomState.actionTypes.zoomIn),
          tap(() => {
            if (visualElementController.canZoomIn()) {
              visualElementController.zoomIn();
            }
          })
        ),
        myActions$.pipe(
          ofActionCreatorType<typeof ZoomState.actions.zoomOut>(ZoomState.actionTypes.zoomOut),
          tap(() => {
            if (visualElementController.canZoomOut()) {
              visualElementController.zoomOut();
            }
          })
        ),
        myActions$.pipe(
          ofActionCreatorType<typeof ZoomState.actions.resetZoom>(ZoomState.actionTypes.resetZoom),
          tap(() => {
            visualElementController.resetView();
          })
        )
      ).pipe(
        // Check zoom capabilities after any of the above observables emit a value
        map(() => ({
          zoomInEnabled: visualElementController.canZoomIn(),
          zoomOutEnabled: visualElementController.canZoomOut(),
        })),
        // Emit action if either value changed
        distinctUntilChanged(
          (prev, next) =>
            prev.zoomInEnabled === next.zoomInEnabled && prev.zoomOutEnabled === next.zoomOutEnabled
        ),
        map((value) => ZoomState.actions.setZoomCapabilities(key, value))
      );

  const loadVisualElement$ = loadGraphElementController(
    visualController,
    visualElementController,
    unload$,
    myActions$,
    state$,
    deps,
    lifecyleActions,
    visualizationManager
  );

  return merge(loadVisualElement$, loadZoomControls$);
};
